import { useState, useRef } from 'react'
import { FileDown, Printer, Loader2, X, FileText, FileArchive } from 'lucide-react'
import jsPDF from 'jspdf'
import JSZip from 'jszip'

interface DocumentExportProps {
  title: string
  content: string
  category: string
  version: number
  lastModified?: string
  author?: string
  onClose?: () => void
}

interface BulkExportDocument {
  id: string
  title: string
  content: string
  category: string
  version: number
  lastModified?: string
  author?: string
}

/**
 * DocumentExport component provides PDF, Markdown, and Print export functionality.
 * Uses jsPDF for direct PDF generation and window.print() as fallback.
 */
export function DocumentExport({
  title,
  content,
  category,
  version,
  lastModified,
  author,
  onClose,
}: DocumentExportProps) {
  const [exporting, setExporting] = useState(false)
  const [exportType, setExportType] = useState<'pdf' | 'markdown' | 'print' | null>(null)
  const printFrameRef = useRef<HTMLIFrameElement>(null)

  const handlePDFExport = async () => {
    setExporting(true)
    setExportType('pdf')

    try {
      await generatePDF({
        title,
        content,
        category,
        version,
        lastModified,
        author,
      })
    } catch (error) {
      console.error('PDF export failed:', error)
      alert('Failed to export PDF. Please try the Print option instead.')
    } finally {
      setExporting(false)
      setExportType(null)
    }
  }

  const handleMarkdownExport = async () => {
    setExporting(true)
    setExportType('markdown')

    try {
      // Add metadata header to markdown
      const header = `# ${title}\n\n` +
        `**Category:** ${formatCategory(category)}  \n` +
        `**Version:** v${version}  \n` +
        (lastModified ? `**Last Modified:** ${lastModified}  \n` : '') +
        (author ? `**Author:** ${author}  \n` : '') +
        `\n---\n\n`

      const fullContent = header + content

      // Create and download file
      const blob = new Blob([fullContent], { type: 'text/markdown;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = sanitizeFilename(title) + '.md'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Markdown export failed:', error)
      alert('Failed to export Markdown. Please try again.')
    } finally {
      setExporting(false)
      setExportType(null)
    }
  }

  const handlePrintExport = async () => {
    setExporting(true)
    setExportType('print')

    try {
      // Create a print-friendly HTML document
      const printContent = generatePrintHTML({
        title,
        content,
        category,
        version,
        lastModified,
        author,
      })

      // Create a hidden iframe for printing
      const iframe = printFrameRef.current
      if (iframe) {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
        if (iframeDoc) {
          iframeDoc.open()
          iframeDoc.write(printContent)
          iframeDoc.close()

          // Wait for content to render
          await new Promise((resolve) => setTimeout(resolve, 500))

          // Trigger print dialog
          iframe.contentWindow?.focus()
          iframe.contentWindow?.print()
        }
      }
    } catch (error) {
      console.error('Print export failed:', error)
      alert('Failed to print document. Please try again.')
    } finally {
      setExporting(false)
      setExportType(null)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900">Export Document</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="space-y-3">
        {/* PDF Export via jsPDF */}
        <button
          onClick={handlePDFExport}
          disabled={exporting}
          className="w-full flex items-center gap-3 px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            {exporting && exportType === 'pdf' ? (
              <Loader2 className="h-5 w-5 text-red-600 animate-spin" />
            ) : (
              <FileDown className="h-5 w-5 text-red-600" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">Export as PDF</p>
            <p className="text-xs text-gray-500">
              Download as a formatted PDF document
            </p>
          </div>
        </button>

        {/* Markdown Export */}
        <button
          onClick={handleMarkdownExport}
          disabled={exporting}
          className="w-full flex items-center gap-3 px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            {exporting && exportType === 'markdown' ? (
              <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
            ) : (
              <FileText className="h-5 w-5 text-purple-600" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">Export as Markdown</p>
            <p className="text-xs text-gray-500">
              Download raw markdown format (.md)
            </p>
          </div>
        </button>

        {/* Print / PDF via browser */}
        <button
          onClick={handlePrintExport}
          disabled={exporting}
          className="w-full flex items-center gap-3 px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            {exporting && exportType === 'print' ? (
              <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            ) : (
              <Printer className="h-5 w-5 text-blue-600" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">Print / Save as PDF</p>
            <p className="text-xs text-gray-500">
              Use browser print dialog (more formatting options)
            </p>
          </div>
        </button>
      </div>

      <p className="mt-4 text-xs text-gray-500">
        Tip: For the best PDF quality, use the first option. For custom layouts, use Print.
      </p>

      {/* Hidden iframe for printing */}
      <iframe
        ref={printFrameRef}
        style={{ display: 'none' }}
        title="Print Frame"
      />
    </div>
  )
}

/**
 * Export button that can be placed in document toolbars
 */
export function DocumentExportButton({
  onClick,
  disabled = false,
}: {
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      title="Export document"
    >
      <FileDown className="h-4 w-4" />
      Export
    </button>
  )
}

/**
 * Bulk export component for multiple documents
 */
export function BulkDocumentExport({
  documents,
  onClose,
  onComplete,
}: {
  documents: BulkExportDocument[]
  onClose: () => void
  onComplete?: () => void
}) {
  const [exporting, setExporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [format, setFormat] = useState<'pdf' | 'markdown'>('pdf')
  const [includeMetadata, setIncludeMetadata] = useState(true)

  const handleBulkExport = async () => {
    if (documents.length === 0) return

    setExporting(true)
    setProgress(0)

    try {
      const zip = new JSZip()
      const folder = zip.folder('documents')

      if (!folder) throw new Error('Failed to create ZIP folder')

      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i]
        const filename = sanitizeFilename(doc.title)

        if (format === 'pdf') {
          // Generate PDF blob
          const pdfBlob = await generatePDFBlob({
            title: doc.title,
            content: doc.content,
            category: doc.category,
            version: doc.version,
            lastModified: doc.lastModified,
            author: doc.author,
          })
          folder.file(`${filename}.pdf`, pdfBlob)
        } else {
          // Generate markdown content
          let mdContent = doc.content
          if (includeMetadata) {
            const header = `# ${doc.title}\n\n` +
              `**Category:** ${formatCategory(doc.category)}  \n` +
              `**Version:** v${doc.version}  \n` +
              (doc.lastModified ? `**Last Modified:** ${doc.lastModified}  \n` : '') +
              (doc.author ? `**Author:** ${doc.author}  \n` : '') +
              `\n---\n\n`
            mdContent = header + doc.content
          }
          folder.file(`${filename}.md`, mdContent)
        }

        setProgress(Math.round(((i + 1) / documents.length) * 100))
      }

      // Add metadata JSON if requested
      if (includeMetadata) {
        const metadata = {
          exportDate: new Date().toISOString(),
          documentCount: documents.length,
          format: format,
          documents: documents.map(d => ({
            title: d.title,
            category: d.category,
            version: d.version,
            lastModified: d.lastModified,
            author: d.author,
          }))
        }
        folder.file('_metadata.json', JSON.stringify(metadata, null, 2))
      }

      // Generate and download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(zipBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `documents-export-${new Date().toISOString().split('T')[0]}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      onComplete?.()
      onClose()
    } catch (error) {
      console.error('Bulk export failed:', error)
      alert('Failed to export documents. Please try again.')
    } finally {
      setExporting(false)
      setProgress(0)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Export Documents</h2>
          </div>
          <button
            onClick={onClose}
            disabled={exporting}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium">{documents.length}</span> document{documents.length !== 1 ? 's' : ''} selected
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Export Format
            </label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={() => setFormat('pdf')}
                  disabled={exporting}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">PDF</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  value="markdown"
                  checked={format === 'markdown'}
                  onChange={() => setFormat('markdown')}
                  disabled={exporting}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Markdown</span>
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeMetadata}
              onChange={(e) => setIncludeMetadata(e.target.checked)}
              disabled={exporting}
              className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Include metadata file</span>
          </label>

          {exporting && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Exporting...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={exporting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleBulkExport}
            disabled={exporting || documents.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <FileArchive className="h-4 w-4" />
                Export to ZIP
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Generate PDF using jsPDF and return as Blob
 */
async function generatePDFBlob(params: {
  title: string
  content: string
  category: string
  version: number
  lastModified?: string
  author?: string
}): Promise<Blob> {
  const { title, content, category, version, lastModified, author } = params
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20
  const contentWidth = pageWidth - (margin * 2)
  let yPosition = margin

  // Helper to add new page if needed
  const checkPageBreak = (neededHeight: number) => {
    if (yPosition + neededHeight > pageHeight - margin) {
      doc.addPage()
      yPosition = margin
      return true
    }
    return false
  }

  // Header - Title
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(10, 38, 71) // Deep Navy
  const titleLines = doc.splitTextToSize(title, contentWidth)
  doc.text(titleLines, margin, yPosition)
  yPosition += (titleLines.length * 7) + 5

  // Header line
  doc.setDrawColor(10, 38, 71)
  doc.setLineWidth(0.5)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 8

  // Metadata
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 100, 100)

  const metaItems: string[] = [
    `Category: ${formatCategory(category)}`,
    `Version: v${version}`,
  ]
  if (lastModified) metaItems.push(`Last Modified: ${lastModified}`)
  if (author) metaItems.push(`Author: ${author}`)

  doc.text(metaItems.join('  |  '), margin, yPosition)
  yPosition += 12

  // Content
  doc.setFontSize(11)
  doc.setTextColor(30, 30, 30)

  const lines = content.split('\n')

  for (const line of lines) {
    const trimmedLine = line.trim()

    // Handle headers
    if (trimmedLine.startsWith('### ')) {
      checkPageBreak(12)
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(50, 50, 50)
      const headerText = trimmedLine.substring(4)
      const headerLines = doc.splitTextToSize(headerText, contentWidth)
      doc.text(headerLines, margin, yPosition)
      yPosition += (headerLines.length * 5) + 4
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(30, 30, 30)
    } else if (trimmedLine.startsWith('## ')) {
      checkPageBreak(14)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 30, 30)
      const headerText = trimmedLine.substring(3)
      const headerLines = doc.splitTextToSize(headerText, contentWidth)
      doc.text(headerLines, margin, yPosition)
      yPosition += (headerLines.length * 6) + 5
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(30, 30, 30)
    } else if (trimmedLine.startsWith('# ')) {
      checkPageBreak(16)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(10, 38, 71)
      const headerText = trimmedLine.substring(2)
      const headerLines = doc.splitTextToSize(headerText, contentWidth)
      doc.text(headerLines, margin, yPosition)
      yPosition += (headerLines.length * 7) + 6
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(30, 30, 30)
    } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      // Bullet point
      checkPageBreak(6)
      const bulletText = trimmedLine.substring(2)
      const bulletLines = doc.splitTextToSize(bulletText, contentWidth - 8)
      doc.text('\u2022', margin, yPosition)
      doc.text(bulletLines, margin + 5, yPosition)
      yPosition += (bulletLines.length * 5) + 2
    } else if (/^\d+\.\s/.test(trimmedLine)) {
      // Numbered list
      checkPageBreak(6)
      const match = trimmedLine.match(/^(\d+\.)\s(.*)$/)
      if (match) {
        const number = match[1]
        const text = match[2]
        const textLines = doc.splitTextToSize(text, contentWidth - 10)
        doc.text(number, margin, yPosition)
        doc.text(textLines, margin + 8, yPosition)
        yPosition += (textLines.length * 5) + 2
      }
    } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
      // Bold line
      checkPageBreak(6)
      doc.setFont('helvetica', 'bold')
      const boldText = trimmedLine.slice(2, -2)
      const boldLines = doc.splitTextToSize(boldText, contentWidth)
      doc.text(boldLines, margin, yPosition)
      yPosition += (boldLines.length * 5) + 2
      doc.setFont('helvetica', 'normal')
    } else if (trimmedLine === '') {
      // Empty line - add small space
      yPosition += 4
    } else if (trimmedLine === '---' || trimmedLine === '***') {
      // Horizontal rule
      checkPageBreak(8)
      yPosition += 3
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.3)
      doc.line(margin, yPosition, pageWidth - margin, yPosition)
      yPosition += 5
    } else {
      // Regular paragraph
      checkPageBreak(6)
      // Handle inline bold
      let processedLine = trimmedLine.replace(/\*\*(.+?)\*\*/g, '$1')
      processedLine = processedLine.replace(/\*(.+?)\*/g, '$1')
      const textLines = doc.splitTextToSize(processedLine, contentWidth)
      doc.text(textLines, margin, yPosition)
      yPosition += (textLines.length * 5) + 2
    }
  }

  // Footer
  const totalPages = doc.internal.pages.length - 1
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(9)
    doc.setTextColor(150, 150, 150)
    doc.text(
      `Page ${i} of ${totalPages}  |  Generated from Clarantis GRC Platform  |  ${new Date().toLocaleDateString()}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    )
  }

  return doc.output('blob')
}

/**
 * Generate and download PDF
 */
async function generatePDF(params: {
  title: string
  content: string
  category: string
  version: number
  lastModified?: string
  author?: string
}): Promise<void> {
  const blob = await generatePDFBlob(params)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = sanitizeFilename(params.title) + '.pdf'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Generate print-friendly HTML for document export
 */
function generatePrintHTML({
  title,
  content,
  category,
  version,
  lastModified,
  author,
}: {
  title: string
  content: string
  category: string
  version: number
  lastModified?: string
  author?: string
}): string {
  // Convert markdown to basic HTML (simple conversion for common patterns)
  const htmlContent = markdownToHTML(content)

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHTML(title)}</title>
  <style>
    @page {
      margin: 1in;
      size: letter;
    }

    * {
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #1a1a1a;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0;
    }

    .header {
      border-bottom: 2px solid #0A2647;
      padding-bottom: 1rem;
      margin-bottom: 2rem;
    }

    .header h1 {
      font-size: 1.5rem;
      font-weight: 700;
      color: #0A2647;
      margin: 0 0 0.5rem 0;
    }

    .metadata {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;
      font-size: 0.875rem;
      color: #666;
    }

    .metadata-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .metadata-label {
      font-weight: 500;
      color: #333;
    }

    .category-badge {
      display: inline-block;
      padding: 0.125rem 0.5rem;
      background: #e5e7eb;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 500;
      color: #374151;
    }

    .content {
      font-size: 0.9375rem;
    }

    .content h1 {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 2rem 0 1rem;
      color: #0A2647;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 0.5rem;
    }

    .content h2 {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 1.5rem 0 0.75rem;
      color: #1a1a1a;
    }

    .content h3 {
      font-size: 1.1rem;
      font-weight: 600;
      margin: 1.25rem 0 0.5rem;
      color: #333;
    }

    .content p {
      margin: 0.75rem 0;
    }

    .content ul, .content ol {
      margin: 0.75rem 0;
      padding-left: 1.5rem;
    }

    .content li {
      margin: 0.25rem 0;
    }

    .content code {
      font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
      font-size: 0.875em;
      background: #f3f4f6;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
    }

    .content pre {
      background: #f3f4f6;
      padding: 1rem;
      border-radius: 0.5rem;
      overflow-x: auto;
      font-size: 0.875rem;
    }

    .content pre code {
      background: transparent;
      padding: 0;
    }

    .content blockquote {
      border-left: 4px solid #0A2647;
      margin: 1rem 0;
      padding: 0.5rem 1rem;
      background: #f9fafb;
      color: #4b5563;
    }

    .content table {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
    }

    .content th, .content td {
      border: 1px solid #e5e7eb;
      padding: 0.5rem 0.75rem;
      text-align: left;
    }

    .content th {
      background: #f9fafb;
      font-weight: 600;
    }

    .footer {
      margin-top: 3rem;
      padding-top: 1rem;
      border-top: 1px solid #e5e7eb;
      font-size: 0.75rem;
      color: #9ca3af;
      text-align: center;
    }

    @media print {
      .no-print {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${escapeHTML(title)}</h1>
    <div class="metadata">
      <div class="metadata-item">
        <span class="category-badge">${escapeHTML(formatCategory(category))}</span>
      </div>
      <div class="metadata-item">
        <span class="metadata-label">Version:</span>
        <span>v${version}</span>
      </div>
      ${lastModified ? `
      <div class="metadata-item">
        <span class="metadata-label">Last Modified:</span>
        <span>${escapeHTML(lastModified)}</span>
      </div>
      ` : ''}
      ${author ? `
      <div class="metadata-item">
        <span class="metadata-label">Author:</span>
        <span>${escapeHTML(author)}</span>
      </div>
      ` : ''}
    </div>
  </div>

  <div class="content">
    ${htmlContent}
  </div>

  <div class="footer">
    <p>Generated from Clarantis GRC Platform</p>
    <p>Exported on ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}</p>
  </div>
</body>
</html>
  `
}

/**
 * Simple markdown to HTML converter for common patterns
 */
function markdownToHTML(markdown: string): string {
  if (!markdown) return '<p><em>No content</em></p>'

  let html = escapeHTML(markdown)

  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>')
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>')
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>')

  // Bold and italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
  html = html.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>')
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>')
  html = html.replace(/_(.+?)_/g, '<em>$1</em>')

  // Code blocks
  html = html.replace(/```[\s\S]*?```/g, (match) => {
    const code = match.replace(/```\w*\n?/g, '').replace(/```$/g, '')
    return `<pre><code>${code}</code></pre>`
  })

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')

  // Blockquotes
  html = html.replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')

  // Unordered lists (handle nested items)
  html = html.replace(/^[-*] (.+)$/gm, '<li>$1</li>')
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>')

  // Horizontal rules
  html = html.replace(/^---+$/gm, '<hr>')
  html = html.replace(/^\*\*\*+$/gm, '<hr>')

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

  // Paragraphs - wrap lines that aren't already in tags
  const lines = html.split('\n')
  const wrappedLines = lines.map((line) => {
    const trimmed = line.trim()
    if (!trimmed) return ''
    if (trimmed.match(/^<(h[1-6]|ul|ol|li|pre|blockquote|hr|p)/)) {
      return line
    }
    return `<p>${line}</p>`
  })

  html = wrappedLines.join('\n')

  // Clean up empty paragraphs
  html = html.replace(/<p>\s*<\/p>/g, '')

  return html
}

/**
 * Escape HTML special characters
 */
function escapeHTML(str: string): string {
  const escapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }
  return str.replace(/[&<>"']/g, (char) => escapeMap[char])
}

/**
 * Format category for display
 */
function formatCategory(category: string): string {
  const categoryLabels: Record<string, string> = {
    'policy': 'Policy',
    'procedure': 'Procedure',
    'standard': 'Standard',
    'guideline': 'Guideline',
    'compensating-control': 'Compensating Control',
    'diagram': 'Diagram/Image',
  }
  return categoryLabels[category] || category
}

/**
 * Sanitize filename for safe file downloads
 */
function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 100)
}

export default DocumentExport
